import { useEffect, useState } from "react";
import { Download, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

export const coverDraw = (x, img, w, h) => {
  const s = Math.max(w / img.width, h / img.height);
  const dw = img.width * s;
  const dh = img.height * s;
  x.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
};

// Shared 1080x1350 cinematic frame: scene poster + dark gradients + ember border + brand header/footer.
export const cardBase = async (posterSrc) => {
  const c = document.createElement("canvas");
  c.width = 1080;
  c.height = 1350;
  const x = c.getContext("2d");
  x.fillStyle = "#FFFFFF";
  x.fillRect(0, 0, 1080, 1350);
  try {
    coverDraw(x, await loadImage(posterSrc), 1080, 1350);
  } catch (e) { console.warn("Share card poster failed to load:", posterSrc, e); }
  const g = x.createLinearGradient(0, 400, 0, 1350);
  g.addColorStop(0, "rgba(5,5,5,0)");
  g.addColorStop(0.55, "rgba(5,5,5,0.82)");
  g.addColorStop(1, "rgba(5,5,5,0.97)");
  x.fillStyle = g;
  x.fillRect(0, 0, 1080, 1350);
  const top = x.createLinearGradient(0, 0, 0, 260);
  top.addColorStop(0, "rgba(5,5,5,0.8)");
  top.addColorStop(1, "rgba(5,5,5,0)");
  x.fillStyle = top;
  x.fillRect(0, 0, 1080, 260);
  x.strokeStyle = "rgba(245,158,11,0.85)";
  x.lineWidth = 3;
  x.strokeRect(44, 44, 992, 1262);
  x.textAlign = "center";
  x.fillStyle = "#F59E0B";
  x.font = "26px 'Courier New', monospace";
  x.fillText("T . A . C . C .", 540, 130);
  x.fillStyle = "#44403C";
  x.font = "18px 'Courier New', monospace";
  x.fillText("T H R O U G H   A D V E R S I T Y   C O M E S   C H A N G E", 540, 170);
  x.fillStyle = "#F59E0B";
  x.font = "22px 'Courier New', monospace";
  x.fillText("T A C C . L O V E", 540, 1240);
  return { canvas: c, ctx: x };
};

// Generic share dialog: `draw()` returns a canvas; Share uses the phone share sheet, falls back to PNG download.
export const ShareCardDialog = ({ open, onOpenChange, draw, title, description, fileName, shareText, testId = "share-card", story = false }) => {
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    draw().then((c) => setPreview(c.toDataURL("image/png")));
  }, [open, draw]);

  const download = async () => {
    const a = document.createElement("a");
    a.href = (await draw()).toDataURL("image/png");
    a.download = fileName;
    a.click();
    toast.success("Card saved.");
  };

  const share = async () => {
    const c = await draw();
    c.toBlob(async (blob) => {
      const file = new File([blob], fileName, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title, text: shareText });
        } catch (e) { if (e?.name !== "AbortError") console.warn("Share failed:", e); }
        return;
      }
      download();
    }, "image/png");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-[#F59E0B]/40 text-[#1C1917] max-w-md p-6" data-testid={`${testId}-dialog`}>
        <DialogTitle className="font-display text-2xl tracking-tight">{title}</DialogTitle>
        <DialogDescription className="text-[#78716C] text-sm">{description}</DialogDescription>
        <div className={`${story ? "aspect-[9/16] max-h-[60vh] mx-auto" : "aspect-[4/5]"} w-full bg-black/60 border border-[#E7DFD5] flex items-center justify-center overflow-hidden`}>
          {preview ? (
            <img src={preview} alt={title} className="w-full h-full object-contain" data-testid={`${testId}-preview`} />
          ) : (
            <Loader2 size={22} className="animate-spin text-[#B45309]" />
          )}
        </div>
        <div className="flex flex-wrap gap-3 pt-2">
          <button onClick={share} disabled={!preview} data-testid={`${testId}-btn`} className="btn-ember text-xs disabled:opacity-50">
            <Share2 size={13} /> Share it
          </button>
          <button onClick={download} disabled={!preview} data-testid={`${testId}-download`} className="btn-outline text-xs disabled:opacity-50">
            <Download size={13} /> Save the card
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
